<template>
  <!--
    The frame around the records, not a field inside one: one block across the
    top of the summary stating the account every record on this step will post
    to. Amber-bordered while the step still owes something, so "no account yet"
    is visible from the same glance that reads the cards.
  -->
  <div
    class="flex flex-col md:flex-row md:items-center gap-3.5 p-4 bg-background border rounded-ds-lg overflow-hidden"
    :class="owed ? 'border-warning' : 'border-border'"
  >
    <div class="flex gap-3.5 flex-auto">
      <span
        class="inline-flex items-center justify-center size-9 shrink-0 bg-card border border-border rounded-ds-md"
      >
        <Folder
          class="size-4.5"
          :class="name ? 'text-brand' : 'text-muted-foreground'"
        />
      </span>

      <div class="flex-1 min-w-0">
        <!--
        The dot rather than the dot and the word "required": it is the same mark
        the rail's legend explains, and the line under it already says "Not set"
        in words.
      -->
        <DSFieldLabel
          class="mb-0.5"
          label="Account"
          :mark
        />

        <p
          class="text-base font-bold"
          :class="name ? 'text-foreground' : 'text-warning-subtle-foreground'"
        >
          {{ name || 'Not set' }}
        </p>

        <p
          v-if="detail"
          class="text-xs font-medium text-muted-foreground"
        >
          {{ detail }}
        </p>
      </div>
    </div>
    <!--
      Read-only, with a way back: the summary is a place to check, and the one
      step that sets the account is where it stays editable. Named after that
      step rather than "Edit" so the two halves of the ownership question — pick
      one, or create one — are not implied to be here.
    -->
    <DSButton
      type="button"
      variant="link"
      size="sm"
      class="shrink-0 h-auto px-0 font-bold justify-start"
      @click="emit('edit')"
    >
      Set in {{ stepLabel }}
      <ArrowRight class="size-3" />
    </DSButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowRight, Folder } from '@lucide/vue'

import { DSButton, DSFieldLabel, type FieldLabelMark } from '@ds'

import { WizardFieldOrigin } from '../utils/types'

/*
 * The account both halves of the ownership step write, read back on the
 * summary.
 *
 * One block for two shapes: picking an existing account copies its name and
 * type in beside its id, and creating one leaves the id empty — so the name is
 * what there is to state either way, and `isNew` is the only thing that has to
 * be said differently. Nothing here is a control: the values are the ownership
 * step's, and this is the way back to it.
 */
const { name, type, isNew, owed, origin } = defineProps<{
  /** The account's name, from whichever half of the ownership step set it. */
  name?: string
  /** Its ownership type, spelled as the repository spells it. */
  type?: string
  /** The step is set to create this account alongside the records. */
  isNew?: boolean
  /**
   * The ownership step still owes a required field. Carries the amber border
   * and the required dot — a name with no type yet is owed too, which is why
   * this is passed in rather than derived from the name alone.
   */
  owed?: boolean
  /** Where the account was set, for the sparkle — see WizardFieldOrigin. */
  origin?: WizardFieldOrigin
  /** The ownership step's own label, so a rename cannot leave this quoting it. */
  stepLabel: string
}>()

const emit = defineEmits<{
  (e: 'edit'): void
}>()

/*
 * Two marks, not four. Every wizard that asks the ownership question requires
 * an account — which of the three fields carries that requirement is what the
 * mode decides, and `ownerAccountName` reads OPTIONAL the moment an existing
 * account is picked. Passing the field's own origin through `markFor` therefore
 * printed "optional" over an account the create cannot go without.
 *
 * So: amber dot while it is owed, sparkle when the assistant resolved it,
 * nothing when the user set it themselves.
 */
const mark = computed<FieldLabelMark | undefined>(() => {
  if (owed) return 'required'
  return origin === WizardFieldOrigin.READ ? 'ai' : undefined
})

/*
 * What kind of account this is, under its name. An account being created is
 * said out loud: until create runs it does not exist, and a name alone would
 * read as one the repository already has.
 */
const detail = computed(() => {
  if (!name) return undefined
  if (isNew) return type ? `New account · ${type}` : 'New account'
  return type
})
</script>
